import { useContext, useState } from "react";
import { useSelector } from "react-redux";
import { Table, Select, Popover } from "antd";
import { ColumnsType } from "antd/es/table/Table";
import { DeleteOutlined } from "@ant-design/icons";

import { Loading } from "@/shared/components/Loading/Loading";
import { selectClientRoles, selectAssessmentRoles } from "@/shared/services/store/rolesStore";
import { AssessmentAbilityContext } from "@/shared/contexts/ability.context";
import { Action, Subject } from "@/shared/services/permissions/casl-ability.factory";

import { makeReadableName } from "@/shared/utils/common.utils";
import { isSubstring } from "@/shared/utils/comparison/comparison";

import { useAssessmentHook } from "@/shared/hooks/assessments/useAssessment.hook";
import { useGetClientUsersHook } from "@/shared/hooks/users/use-get-client-users.hook";

import { Button, ButtonSize } from "@/ui-components/commonComponents/Button/Button";

import { IUserForTable } from "@/modules/client/clients.interface";
import { ClientRoleEnum } from "@/modules/client/client-roles.interface";
import {
  assessmentRoleDefinitions,
  AssessmentRoleEnum,
} from "@/modules/assessments/services/assessment-permissions-http.service";
import { getAssessmentRoleOptions } from "@/modules/assessments/scenes/AssessmentTeam/assessment-roles.utils";

import { useRemoveAssessmentUserHook } from "@/modules/assessments/hooks/use-remove-assessment-user.hook";
import { useManageAssessmentUserHook } from "@/modules/assessments/hooks/use-manage-assessment-user.hook";
import { useGetAssessmentUsersPermissionsHook } from "@/modules/assessments/hooks/use-get-assessment-users-permissions.hook";
import { RoleInfoButton } from "@/shared/components/RoleInfoButton/RoleInfoButton";
import { ViewRolesModal } from "@/shared/components/ViewRolesModal/ViewRolesModal";

import styles from "./team-list.styles.module.css";

export type TeamListComponentProps = {
  assessmentId: number;
  isArchived?: boolean;
};

export function TeamListComponent({ assessmentId, isArchived }: TeamListComponentProps) {
  const [isRolesModalOpen, setIsRolesModalOpen] = useState(false);

  const {
    assessmentPermissions,
    isLoading: isLoadingAssessmentPermissions,
    refreshAssessmentPermissions,
  } = useGetAssessmentUsersPermissionsHook(assessmentId);

  // TODO: Move to global state
  const { assessment, isLoading: isLoadingAssessment } = useAssessmentHook(Number(assessmentId));
  const { users, isLoading: isLoadingClientUsers } = useGetClientUsersHook(assessment?.clientId);

  const { manageAssessmentUser, isLoading: isLoadingUserToAssessment } = useManageAssessmentUserHook();
  const { removeAssessmentUser, isLoading: isLoadingRemoveAction } = useRemoveAssessmentUserHook();

  const updateUserRoleAndRefresh = async ({ userId, roleName }: { userId: number; roleName: AssessmentRoleEnum }) => {
    await manageAssessmentUser(Number(assessmentId), Number(userId), roleName);
    await refreshAssessmentPermissions();
  };

  const removeUserRoleAndRefresh = async ({ userId, assessmentId }: { userId: number; assessmentId: number }) => {
    await removeAssessmentUser(assessmentId, userId);
    await refreshAssessmentPermissions();
  };

  const clientRoles = useSelector(selectClientRoles);
  const assessmentRoles = useSelector(selectAssessmentRoles);

  const assessmentAbility = useContext(AssessmentAbilityContext);
  const canRemoveAssessmentUser = assessmentAbility.can(Action.Delete, Subject.ASSESSMENT_USER_PERMISSION);
  const canUpdateAssessmentUser = assessmentAbility.can(Action.Update, Subject.ASSESSMENT_USER_PERMISSION);

  const assessmentUsersTableColumns: ColumnsType<IUserForTable> = [
    {
      key: "fullName",
      title: "User",
      dataIndex: "fullName",
      width: "50%",
      defaultSortOrder: "ascend",
    },
    {
      key: "roleName",
      title: (
        <>
          Role In This Assessment
          <RoleInfoButton onClick={() => setIsRolesModalOpen(true)} />
        </>
      ),
      width: "45%",
      dataIndex: "roleName",
      render: (value, record) =>
        record.isClientLevelRole ? (
          <b>Client {record.roleName}</b>
        ) : (
          <Select
            showSearch={true}
            placeholder="Select a role"
            disabled={!canUpdateAssessmentUser || isArchived}
            options={getAssessmentRoleOptions(assessmentRoles).map(({ value, name }) => ({ value, label: name }))}
            optionFilterProp="children"
            filterOption={(input, option) => isSubstring(String(option?.label), input)}
            onChange={(roleName) => updateUserRoleAndRefresh({ roleName, userId: Number(record.userId) })}
            value={value}
            className={styles.selectContainer}
            dropdownMatchSelectWidth={false}
          />
        ),
    },
    {
      key: "userId",
      title: "",
      dataIndex: "userId",
      width: "4%",
      render: (userId, record) =>
        !record.isClientLevelRole && (
          <Popover
            trigger="click"
            content={
              <Button
                disabled={!canRemoveAssessmentUser || isArchived}
                buttonSize={ButtonSize.small}
                isLoading={isLoadingUserToAssessment}
                onClick={() => removeUserRoleAndRefresh({ assessmentId, userId })}
              >
                Remove
              </Button>
            }
          >
            <DeleteOutlined disabled={isArchived} />
          </Popover>
        ),
    },
  ];

  if (isLoadingAssessmentPermissions || isLoadingAssessment || isLoadingClientUsers) {
    return <Loading />;
  }

  const { clientUserAdmins, clientUserAssessmentManagers } = users.reduce(
    (acc, user) => {
      // There can only be one client user per user and client, so grab the first one
      const clientUserRole = clientRoles.find((role) => role.roleId === user.clientUsers?.[0].roleId);
      if (!clientUserRole) {
        return acc;
      }

      const tableUser = {
        key: user.userId,
        userId: user.userId,
        fullName: user.fullName,
        roleName: makeReadableName(clientUserRole.name),
        isClientLevelRole: true,
      };

      if (clientUserRole?.name === ClientRoleEnum.ADMIN) {
        acc.clientUserAdmins.push(tableUser);
      } else if (clientUserRole?.name === ClientRoleEnum.ASSESSMENT_MANAGER) {
        acc.clientUserAssessmentManagers.push(tableUser);
      }

      return acc;
    },
    { clientUserAdmins: [] as IUserForTable[], clientUserAssessmentManagers: [] as IUserForTable[] },
  );

  const assessmentUsers = assessmentPermissions
    .filter(
      ({ userId, user }) =>
        !user?.isAdmin && !clientUserAdmins.find((clientUserAdmin) => clientUserAdmin.userId === userId),
    )
    .map(({ user, role }) => ({
      roleName: makeReadableName(role?.name || AssessmentRoleEnum.HIRING_MANAGER),
      fullName: user?.fullName || "",
      userId: user?.userId || 0,
      key: user?.userId || 0,
      isClientLevelRole: false,
    }));

  return (
    <div className={styles.teamListContainer}>
      <Table
        loading={isLoadingUserToAssessment || isLoadingRemoveAction}
        columns={assessmentUsersTableColumns}
        dataSource={[...clientUserAdmins, ...clientUserAssessmentManagers, ...assessmentUsers]}
        scroll={{ x: "1000px" }}
        style={{ width: "100%" }}
      />
      <ViewRolesModal
        isOpen={isRolesModalOpen}
        onClose={() => setIsRolesModalOpen(false)}
        title="Assessment Roles"
        roles={assessmentRoleDefinitions}
      />
    </div>
  );
}
